import { KeyRound, LockKeyhole, Mail, ShieldCheck } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { apiRequest, ApiRequestError } from "../lib/api";

type Props = {
  role: "client" | "agent";
};

export function LoginPage({ role }: Props) {
  const { session, loading, login, verifyAgentTwoFactor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [resetMode, setResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");

  const isAgent = role === "agent";
  const pending2FA = isAgent && session?.twoFactorRequired === true;

  if (!loading && session?.authenticated && session.user) {
    const target = session.user.role === "client" ? "/" : "/desk";
    return <Navigate to={target} replace />;
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setMessage(null);

    try {
      if (pending2FA) {
        const next = await verifyAgentTwoFactor(token);
        if (next.authenticated) {
          navigate("/desk", { replace: true });
        }
        return;
      }

      const next = await login(role, { username, password });

      if (next.twoFactorRequired) {
        setMessage("Enviamos um código de verificação. Informe-o para continuar.");
        return;
      }

      const requested = (location.state as { from?: string } | null)?.from;
      navigate(requested || (isAgent ? "/desk" : "/"), { replace: true });
    } catch (error) {
      setMessage(
        error instanceof ApiRequestError
          ? error.message
          : "Não foi possível entrar. Tente novamente.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const requestReset = async (event: FormEvent) => {
    event.preventDefault();
    if (!session?.csrfToken) return;

    setSubmitting(true);
    setMessage(null);

    try {
      const result = await apiRequest<{ sent: boolean; message: string }>(
        "/auth/client/password-reset",
        {
          method: "POST",
          csrfToken: session.csrfToken,
          body: { username: resetEmail },
        },
      );
      setMessage(result.message);
    } catch (error) {
      setMessage(
        error instanceof ApiRequestError
          ? error.message
          : "Não foi possível solicitar a recuperação agora.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-panel">
        <div className="login-brand">
          <img className="login-brand-logo" src="/brand/noverik-logo.png" alt="Noverik Tecnologia" />
          <span className="login-product">SERVICE DESK</span>
        </div>

        <div className="login-copy">
          <span className="eyebrow">{isAgent ? "CENTRAL DO ATENDENTE" : "ÁREA DO CLIENTE"}</span>
          <h1>{pending2FA ? "Confirme seu acesso." : "Suporte de TI sem complicação."}</h1>
          <p>
            {pending2FA
              ? "Digite o código de verificação enviado pelo método configurado no osTicket."
              : isAgent
                ? "Acesse filas, chamados, clientes e toda a operação de atendimento."
                : "Abra seus chamados, acompanhe o andamento e mantenha sua empresa sempre em movimento."}
          </p>

          <div className="login-benefits">
            <div><ShieldCheck size={18} /><span>Sessão integrada ao osTicket</span></div>
            <div><KeyRound size={18} /><span>Acesso protegido e centralizado</span></div>
          </div>
        </div>
      </section>

      <section className="login-form-side">
        <form className="login-card" onSubmit={resetMode ? requestReset : submit}>
          <div className="login-card-head">
            <span>{isAgent ? "NOVERIK DESK" : "CENTRAL DE ATENDIMENTO"}</span>
            <h2>
              {pending2FA
                ? "Verificação em duas etapas"
                : resetMode
                  ? "Recuperar senha"
                  : "Acesse sua conta"}
            </h2>
            <p>
              {pending2FA
                ? "Informe o código para concluir o login."
                : resetMode
                  ? "Informe seu e-mail. Se houver uma conta ativa, enviaremos as instruções de recuperação."
                  : "Use as mesmas credenciais cadastradas no atendimento."}
            </p>
          </div>

          {resetMode && !isAgent ? (
            <label className="field">
              <span>E-mail</span>
              <div className="input-wrap">
                <Mail size={17} />
                <input
                  autoFocus
                  type="email"
                  autoComplete="email"
                  value={resetEmail}
                  onChange={(event) => setResetEmail(event.target.value)}
                  placeholder="voce@empresa.com.br"
                  required
                />
              </div>
            </label>
          ) : pending2FA ? (
            <label className="field">
              <span>Código de verificação</span>
              <div className="input-wrap">
                <KeyRound size={17} />
                <input
                  autoFocus
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  value={token}
                  onChange={(event) => setToken(event.target.value)}
                  placeholder="Digite o código"
                />
              </div>
            </label>
          ) : (
            <>
              <label className="field">
                <span>{isAgent ? "Usuário ou e-mail" : "E-mail"}</span>
                <div className="input-wrap">
                  <Mail size={17} />
                  <input
                    autoFocus
                    autoComplete="username"
                    value={username}
                    onChange={(event) => setUsername(event.target.value)}
                    placeholder={isAgent ? "Seu usuário ou e-mail" : "voce@empresa.com.br"}
                  />
                </div>
              </label>

              <label className="field">
                <span>Senha</span>
                <div className="input-wrap">
                  <LockKeyhole size={17} />
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Digite sua senha"
                  />
                </div>
              </label>
            </>
          )}

          {message && <div className="login-message">{message}</div>}

          <button className="button primary login-submit" disabled={submitting}>
            {submitting
              ? resetMode
                ? "Enviando..."
                : "Validando..."
              : resetMode
                ? "Enviar recuperação"
                : pending2FA
                  ? "Confirmar código"
                  : "Entrar"}
          </button>

          {!isAgent && !pending2FA && (
            <button
              type="button"
              className="login-forgot"
              onClick={() => {
                setResetMode((value) => !value);
                setMessage(null);
                if (!resetMode && username) setResetEmail(username);
              }}
            >
              {resetMode ? "Voltar para o login" : "Esqueci minha senha"}
            </button>
          )}

          <div className="login-switch">
            {isAgent ? (
              <a href="/login">Sou cliente</a>
            ) : (
              <a href="/desk/login">Acesso do atendente</a>
            )}
          </div>
        </form>
      </section>
    </main>
  );
}
